import { BrandCard } from "./styles/components/BrandCard/BrandCard.mjs";
import { getBadges } from "./styles/components/BrandCard/BrandCard.mjs";
import { AddAssetButton, BrandLogo, ReplaceLogo } from "./styles/components/BrandLogo/BrandLogo.mjs";
import { getCommonBrandCardProps, SelectBrandPanel } from "./styles/components/BrandSelector/SelectBrandPanel/SelectBrandPanel.mjs";
import { jsx, jsxs } from "react/jsx-runtime";
import { useMutation, useQuery } from "@apollo/client";
import { BrandStatus, KeyType } from "@seek/brand-types";
import { useTranslations } from "@vocab/react";
import { Box, Stack, FieldMessage, Columns, Column, TabsProvider, Tabs, TabPanels, TabPanel, Tab, Alert, Text, Loader, Button, IconImage, ButtonLink, IconEdit, Heading, Hidden, Inline, IconArrow, Actions } from "braid-design-system";
import isEmpty from "lodash/isEmpty.js";
import { useEffect, useState, memo, useCallback } from "react";
import gql from "graphql-tag";
import { translations } from "./components/.vocab/index.mjs";
import isEqual from "lodash/isEqual.js";
import isUndefined from "lodash/isUndefined.js";
import { FileUploader } from "./styles/components/private/FileUploader/FileUploader.mjs";
import get from "lodash/get.js";
import { StrategiesEnum } from "@seek/image-service-types";
import forEach from "lodash/forEach.js";
import compact from "lodash/compact.js";
import sortBy from "lodash/sortBy.js";
import uniqBy from "lodash/uniqBy.js";
import { CoverImage } from "./styles/components/CoverImage/CoverImage.mjs";
import { UploadAssetButton } from "./styles/components/UploadAssetButton/UploadAssetButton.mjs";
import { adapter } from "@seek/tealium-adapter";
import { Carousel } from "./styles/components/CompanyProfileCarousel/components/Carousel/Carousel.mjs";
import { Placeholder } from "./styles/components/CompanyProfileCarousel/components/Placeholder/Placeholder.mjs";
import { buildUrlWithQueryParams } from "./styles/components/CompanyProfileCarousel/components/CompanyProfileCards/CompanyProfileCards.mjs";
const AssetsFragment = gql`
  fragment assetFields on BrandAssets {
    logo {
      id
      url
      strategies
    }
    cover {
      id
      url
      strategies
    }
  }
`;
const BrandDetailsFragment = gql`
  fragment brandDetails on Brand {
    id
    legacyId
    owner {
      id
      type
    }
    name
    state
    isDefault
    isLiveOnAd
    createdOn
    assets {
      ...assetFields
    }
  }
  ${AssetsFragment}
`;
const ListBrandsQuery = gql`
  query ListBrands($owner: BrandOwnerInput!) {
    brands(owner: $owner) {
      ...brandDetails
    }
  }
  ${BrandDetailsFragment}
`;
const getImageServiceEndpoint = ({ isProd }) => `https://image-service-api${isProd ? "" : ".staging"}.cloud.seek.com.au/client`;
var Usage = /* @__PURE__ */ ((Usage2) => {
  Usage2["logo"] = "logo";
  Usage2["cover"] = "cover";
  return Usage2;
})(Usage || {});
const uploadAsset = async ({
  image,
  getToken,
  isProd,
  strategies,
  usage
}) => {
  const token = getToken && await getToken();
  if (!token) {
    throw new Error("Failed to get token");
  }
  const formData = new FormData();
  const uploadParams = {
    image,
    metadata: JSON.stringify({ usage }),
    strategies: JSON.stringify(strategies)
  };
  forEach(uploadParams, (value, key) => value && formData.append(key, value));
  const response = await fetch(getImageServiceEndpoint({ isProd }), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`
    },
    body: formData
  });
  const respBody = await (response == null ? void 0 : response.json());
  if (!response.ok) {
    const respStatus = response == null ? void 0 : response.status;
    const respMessage = respStatus === 400 ? respBody == null ? void 0 : respBody.message : "";
    throw new Error(respMessage || "Failed to upload logo");
  }
  return respBody;
};
const getLogoStrategies = (croppedInfo) => [
  {
    name: StrategiesEnum.JDP_LOGO_LEGACY,
    ...croppedInfo
  },
  {
    name: StrategiesEnum.SERP_LOGO_LEGACY
  },
  {
    name: StrategiesEnum.SERP_LOGO
  },
  {
    name: StrategiesEnum.JDP_LOGO
  }
];
const uploadLogo = async ({
  image,
  getToken,
  isProd,
  croppedInfo
}) => uploadAsset({
  image,
  getToken,
  isProd,
  strategies: getLogoStrategies(croppedInfo),
  usage: Usage.logo
});
const AddLogoFile = ({ dataTestId, onFileSelected, onClick }) => {
  const { t } = useTranslations(translations);
  const buttonProps = {
    dataTestId,
    onClick
  };
  return /* @__PURE__ */ jsx(AddAssetButton, { label: t("Add logo"), ...buttonProps, children: /* @__PURE__ */ jsx(FileUploader, { onFileSelected }) });
};
const BrandUpdateMutationFragment = gql`
  fragment updateBrandDetailsFragment on UpdateBrandPayload {
    ... on UpdateBrandSuccessPayload {
      id
      legacyId
      owner {
        id
        type
      }
      name
      state
      isDefault
      assets {
        ...assetFields
      }
    }
    ... on InvalidInputPayload {
      reason
    }
  }
  ${AssetsFragment}
`;
const BrandCreateMutationFragment = gql`
  fragment createBrandDetailsFragment on CreateBrandPayload {
    ... on CreateBrandSuccessPayload {
      id
      legacyId
      owner {
        id
        type
      }
      name
      state
      isDefault
      assets {
        ...assetFields
      }
    }
    ... on InvalidInputPayload {
      reason
    }
  }
  ${AssetsFragment}
`;
const CreateBrandMutation = gql`
  mutation createBrand($input: CreateBrandInput!) {
    createBrand(input: $input) {
      ...createBrandDetailsFragment
    }
  }
  ${BrandCreateMutationFragment}
`;
const UpdateBrandMutation = gql`
  mutation updateBrand($input: UpdateBrandInput!) {
    updateBrand(input: $input) {
      ...updateBrandDetailsFragment
    }
  }
  ${BrandUpdateMutationFragment}
`;
const useSaveBrand = ({ asset, owner, brandId, legacyId }) => {
  const isUpdateMutation = Boolean(brandId || legacyId);
  const operationName = isUpdateMutation ? "updateBrand" : "createBrand";
  const saveBrandMutation = isUpdateMutation ? UpdateBrandMutation : CreateBrandMutation;
  const [saveBrand, { data, error, loading }] = useMutation(saveBrandMutation);
  useEffect(() => {
    const changeBrand = () => {
      if (isUpdateMutation && !brandId) {
        return;
      }
      const logo = {
        id: asset == null ? void 0 : asset.id,
        url: asset == null ? void 0 : asset.url,
        strategies: asset == null ? void 0 : asset.strategies
      };
      return saveBrand({
        variables: {
          input: {
            owner,
            ...isUpdateMutation ? { id: brandId } : {},
            assets: {
              logo
            }
          }
        }
      });
    };
    if (asset) {
      changeBrand();
    }
  }, [asset]);
  return {
    data: get(data, operationName),
    error: error || (data == null ? void 0 : data.reason),
    loading
  };
};
const useUploadImage = ({
  image,
  getToken,
  isProd
}) => {
  const [imageServiceResponse, setImageServiceResponse] = useState();
  const [loading, setLoading] = useState();
  const [error, setError] = useState();
  useEffect(() => {
    let loaded = false;
    const upload = async () => {
      const uploadResponse = await uploadLogo({
        image,
        getToken,
        isProd: Boolean(isProd)
      });
      if (!loaded && uploadResponse) {
        setImageServiceResponse(uploadResponse);
      }
    };
    if (image) {
      setLoading(true);
      setError(void 0);
      upload().then(() => setLoading(false)).catch((e) => {
        setError(e);
        setLoading(false);
      });
    }
    return () => {
      loaded = true;
    };
  }, [image]);
  return { data: imageServiceResponse, loading, error };
};
const AddBrandingAsset = memo(
  (props) => {
    const {
      onBrandSaved,
      onClick,
      onReplace,
      getToken,
      isProd,
      owner,
      onNewBrandUpdated
    } = props;
    const [imageState, setImageState] = useState({});
    const { imageFile, imageBlobUrl } = imageState;
    const [nextBrand, setNextBrand] = useState();
    const [showExistingBrandLogo, setShowExistingBrandLogo] = useState(
      Boolean(nextBrand == null ? void 0 : nextBrand.legacyId)
    );
    const invokeCallback = useCallback(
      (result) => {
        if (onNewBrandUpdated) {
          onNewBrandUpdated({ isCreated: !nextBrand });
        }
        setNextBrand(result.data);
        if (onBrandSaved) {
          onBrandSaved(result);
        }
      },
      [onBrandSaved, onNewBrandUpdated, nextBrand]
    );
    const trackReplaceClick = () => {
      if (onReplace) {
        onReplace();
      }
    };
    const onReplaceClick = (im) => {
      setImageState({ imageFile: im, imageBlobUrl: URL.createObjectURL(im) });
      setShowExistingBrandLogo(false);
    };
    const {
      data: imageData,
      loading: imageLoading,
      error: imageError
    } = useUploadImage({ image: imageFile, getToken, isProd });
    const { data: brandData, error: brandError } = useSaveBrand({
      asset: imageData,
      owner,
      brandId: nextBrand == null ? void 0 : nextBrand.id,
      legacyId: nextBrand == null ? void 0 : nextBrand.legacyId
    });
    useEffect(() => {
      if (brandData && showExistingBrandLogo) {
        setNextBrand(brandData);
      }
    }, [brandData, showExistingBrandLogo]);
    const error = imageError || brandError;
    const isUploadFinished = brandData && (imageState == null ? void 0 : imageState.imageFile) && imageLoading === false && !isEqual(nextBrand, brandData);
    useEffect(() => {
      if (isUploadFinished) {
        if (error) {
          invokeCallback({ error });
        } else {
          invokeCallback({ data: brandData });
        }
      }
    }, [brandData, error, invokeCallback, isUploadFinished]);
    const { t } = useTranslations(translations);
    const logo = imageData;
    const shouldShowAddLogoButton = !imageFile && !logo && isUndefined(imageLoading) || imageError;
    return /* @__PURE__ */ jsxs(Box, { "data-testid": props.dataTestId, height: "full", children: [
      /* @__PURE__ */ jsxs(Box, { marginBottom: "small", height: "full", children: [
        /* @__PURE__ */ jsxs(Box, { position: "relative", height: "full", children: [
          !imageError && /* @__PURE__ */ jsx(BrandLogo, { directUrl: imageBlobUrl, withPlaceholder: false }),
          logo && /* @__PURE__ */ jsxs(Box, { position: "relative", onClick: trackReplaceClick, children: [
            /* @__PURE__ */ jsx(ReplaceLogo, {}),
            /* @__PURE__ */ jsx(FileUploader, { onFileSelected: onReplaceClick })
          ] })
        ] }),
        shouldShowAddLogoButton && /* @__PURE__ */ jsx(Box, { onClick, paddingX: ["xxsmall", "small"], children: /* @__PURE__ */ jsx(
          AddLogoFile,
          {
            onFileSelected: (im) => setImageState({
              imageFile: im,
              imageBlobUrl: URL.createObjectURL(im)
            })
          }
        ) })
      ] }),
      /* @__PURE__ */ jsx(Stack, { space: "small", children: imageError && /* @__PURE__ */ jsx(
        FieldMessage,
        {
          id: "error",
          message: t(
            "Something went wrong! Please try again or contact customer support."
          ),
          tone: "critical"
        }
      ) })
    ] });
  },
  () => true
);
const CreateBrandPanel = memo(
  ({
    onBrandSavedHook,
    getToken,
    isProd,
    owner,
    onAddLogoClick,
    onReplaceLogoClick,
    onNewBrandUpdated,
    showCoverImage = true
  }) => {
    const { t } = useTranslations(translations);
    const LogoRenderer = () => /* @__PURE__ */ jsx(
      AddBrandingAsset,
      {
        ...{
          onBrandSaved: onBrandSavedHook,
          getToken,
          isProd,
          owner,
          onClick: onAddLogoClick,
          onReplace: onReplaceLogoClick,
          onNewBrandUpdated
        }
      }
    );
    return /* @__PURE__ */ jsx(Columns, { space: "small", children: /* @__PURE__ */ jsx(Column, { width: "4/5", children: /* @__PURE__ */ jsx(
      BrandCard,
      {
        ...getCommonBrandCardProps(t),
        selected: true,
        selectable: true,
        LogoRenderer,
        showCoverImage
      }
    ) }) });
  },
  () => true
);
const BxTabs = ({ tabs }) => {
  const renderTab = (tab) => /* @__PURE__ */ jsx(Tab, { children: /* @__PURE__ */ jsx(Box, { padding: "none", cursor: "pointer", onClick: tab.onClick, children: tab.text }) }, tab.text);
  return /* @__PURE__ */ jsx(TabsProvider, { id: "brand-selector-tabs", children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    /* @__PURE__ */ jsx(Tabs, { label: "brandActionTabs", children: tabs.map(renderTab) }),
    /* @__PURE__ */ jsx(TabPanels, { renderInactivePanels: true, children: tabs.map((tab) => /* @__PURE__ */ jsx(TabPanel, { children: tab.PanelContent }, tab.text)) })
  ] }) });
};
const getBrandsToShow = ({
  brands = [],
  brandId,
  legacyId,
  limit
}) => {
  const defaultBrand = brands.find((brand) => brand.isDefault === true);
  const activeNonDefaultBrands = brands.filter(
    ({ state, isDefault }) => state === BrandStatus.ACTIVE && !isDefault
  );
  const existingBrandId = brandId || legacyId;
  const existingAttachedBrand = existingBrandId ? activeNonDefaultBrands.find(
    (brand) => brand.id === existingBrandId || brand.legacyId === legacyId
  ) : void 0;
  const activeBrands = sortBy(activeNonDefaultBrands, "createdOn").reverse();
  const brandsSequence = compact([
    existingAttachedBrand,
    defaultBrand,
    ...activeBrands
  ]);
  const uniqueBrands = uniqBy(brandsSequence, "id");
  const result = limit ? uniqueBrands.splice(0, limit) : uniqueBrands;
  return result.map((b, index) => ({ ...b, position: index + 1 }));
};
const BrandSelector = ({
  onBrandSelected,
  brandId,
  legacyId,
  limit,
  getToken,
  isProd,
  ownerId,
  showCoverImage = true,
  showBrandId = false,
  showName = false,
  // tracking callbacks
  onBrandsLoaded,
  onSelectBrandTabClick,
  onCreateBrandTabClick,
  onNewBrandUpdated,
  onAddLogoClick,
  onReplaceLogoClick
}) => {
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const [createdBrand, setCreatedBrand] = useState();
  const [brands, setBrands] = useState([]);
  const hasBrands = !isEmpty(brands);
  const [isSelecting, setIsSelecting] = useState(true);
  const { t } = useTranslations(translations);
  const owner = {
    id: ownerId,
    type: KeyType.SEEK_ID
  };
  const { data, error, loading } = useQuery(ListBrandsQuery, {
    variables: {
      owner
    },
    skip: hasBrands,
    fetchPolicy: "no-cache"
  });
  const resetToFirstExisting = useCallback(() => {
    const initiallySelectedBrand = brands[0];
    setSelectedBrandId(initiallySelectedBrand.id);
    onBrandSelected({ data: initiallySelectedBrand });
  }, [brands, onBrandSelected]);
  useEffect(() => {
    if (!loading && !isEmpty(data)) {
      const brandsData = data.brands || [];
      const brandsToShow = getBrandsToShow({
        brands: brandsData,
        brandId,
        legacyId,
        limit
      });
      setBrands(brandsToShow);
      if (onBrandsLoaded) {
        onBrandsLoaded({
          numberOfBrandsDisplayed: brandsToShow.length,
          numberOfBrands: brandsData.length
        });
      }
    }
  }, [data, loading, brandId, legacyId, limit, onBrandsLoaded]);
  useEffect(() => {
    if (hasBrands && isSelecting && !selectedBrandId) {
      resetToFirstExisting();
    }
  }, [hasBrands, isSelecting, resetToFirstExisting, selectedBrandId]);
  const onBrandSavedHook = useCallback(
    (brandData) => {
      setCreatedBrand(brandData == null ? void 0 : brandData.data);
      onBrandSelected(brandData);
    },
    [onBrandSelected]
  );
  if (error) {
    onBrandSelected({ error });
    return /* @__PURE__ */ jsx(Alert, { tone: "critical", children: /* @__PURE__ */ jsx(Text, { children: t("Oops, something went wrong") }) });
  }
  if (loading) {
    return /* @__PURE__ */ jsx(Loader, {});
  }
  const selectCreatedBrand = () => {
    if (createdBrand) {
      onBrandSelected({ data: { ...createdBrand, position: 0 } });
    }
  };
  const createNewBrandProps = {
    setCreatedBrand,
    onBrandSelected,
    onBrandSavedHook,
    getToken,
    isProd,
    owner,
    onAddLogoClick,
    onReplaceLogoClick,
    onNewBrandUpdated,
    showCoverImage,
    showBrandId,
    showName
  };
  return /* @__PURE__ */ jsx(Box, { background: "surface", children: /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
    /* @__PURE__ */ jsx(Text, { tone: "secondary", children: hasBrands ? t("Continue with a previously created brand or create a new one") : t(
      "Create your first brand by uploading your company logo. Cover images can be added from the success page, after payment"
    ) }),
    hasBrands ? /* @__PURE__ */ jsx(
      BxTabs,
      {
        tabs: [
          {
            PanelContent: /* @__PURE__ */ jsx(
              SelectBrandPanel,
              {
                ...{
                  brands,
                  setSelectedBrandId,
                  onBrandSelected,
                  selectedBrandId,
                  showCoverImage,
                  showBrandId,
                  showName
                }
              }
            ),
            onClick: () => {
              setIsSelecting(!isSelecting);
              resetToFirstExisting();
              if (onSelectBrandTabClick) {
                onSelectBrandTabClick();
              }
            },
            text: t("Existing brands")
          },
          {
            PanelContent: /* @__PURE__ */ jsx(CreateBrandPanel, { ...createNewBrandProps }),
            onClick: () => {
              setIsSelecting(!isSelecting);
              selectCreatedBrand();
              if (onCreateBrandTabClick) {
                onCreateBrandTabClick();
              }
            },
            text: t("Create new brand")
          }
        ]
      }
    ) : /* @__PURE__ */ jsx(CreateBrandPanel, { ...createNewBrandProps })
  ] }) });
};
const UploadAssetButton2 = ({
  label,
  dataTestId = "bx-upload-asset",
  allowMultiple = false,
  onFileSelected
}) => /* @__PURE__ */ jsxs(Box, { position: "relative", children: [
  /* @__PURE__ */ jsxs(Button, { variant: "ghost", children: [
    /* @__PURE__ */ jsx(IconImage, {}),
    " ",
    label
  ] }),
  /* @__PURE__ */ jsx(
    FileUploader,
    {
      dataTestId,
      onFileSelected,
      allowMultiple
    }
  )
] });
const WriteAReviewButton = ({
  writeAReviewUrl,
  payload
}) => {
  const { t } = useTranslations(translations);
  const tealium = adapter();
  return /* @__PURE__ */ jsx(
    ButtonLink,
    {
      icon: /* @__PURE__ */ jsx(IconEdit, {}),
      tone: "formAccent",
      variant: "solid",
      href: writeAReviewUrl,
      onClick: async () => {
        tealium.link("company_profile_write_review_pressed", {
          siteCountry: payload == null ? void 0 : payload.siteCountry,
          siteLanguage: payload == null ? void 0 : payload.siteLanguage,
          zone: payload == null ? void 0 : payload.zone,
          isLoggedin: payload == null ? void 0 : payload.isLoggedIn,
          siteSection: payload == null ? void 0 : payload.siteSection,
          loginId: payload == null ? void 0 : payload.loginId,
          actionOrigin: "home"
        });
        await new Promise((resolve) => setTimeout(resolve, 500));
      },
      children: t("Write a review")
    }
  );
};
const ReviewsLandingPageHeader = ({
  writeAReviewUrl
}) => {
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsxs(Columns, { space: "none", collapseBelow: "desktop", children: [
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
      /* @__PURE__ */ jsx(Heading, { level: "3", weight: "regular", children: t("Explore companies") }),
      /* @__PURE__ */ jsx(Text, { size: "standard", weight: "regular", tone: "neutral", children: t(
        "Learn about new jobs, reviews, company culture, perks and benefits."
      ) })
    ] }) }),
    /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Hidden, { below: "desktop", children: /* @__PURE__ */ jsx(WriteAReviewButton, { writeAReviewUrl }) }) })
  ] });
};
const DefaultHeader = () => {
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
    /* @__PURE__ */ jsx(Heading, { level: "3", children: t("Find your next employer") }),
    /* @__PURE__ */ jsx(Text, { tone: "secondary", children: t(
      "Explore company profiles to find the right workplace for you. Learn about jobs, reviews, company culture, perks and benefits."
    ) })
  ] });
};
const Header = ({ onReviewsLandingPage, writeAReviewUrl }) => onReviewsLandingPage ? /* @__PURE__ */ jsx(ReviewsLandingPageHeader, { writeAReviewUrl }) : /* @__PURE__ */ jsx(DefaultHeader, {});
const CompanyProfilesQuery = gql`
  query MarqueeCompanies(
    $limit: Int
    $hideReviews: Boolean!
    $hideJobs: Boolean!
  ) {
    marqueeCompanies(limit: $limit, hideJobs: $hideJobs) {
      id
      title
      logo
      reviews @skip(if: $hideReviews) {
        rating
        count
      }
      companyNameSlug
      jobCount @skip(if: $hideJobs)
    }
  }
`;
const CompanyProfileCarousel = ({
  analyticsQueryParams,
  onReviewsLandingPage = false,
  writeAReviewUrl = "",
  discoverMoreUrl = "/companies/",
  hideReviews = false,
  hideJobs = false,
  onError,
  payload
}) => {
  const { t } = useTranslations(translations);
  const numCompanyProfiles = 15;
  const tealium = adapter();
  const { data, error, loading } = useQuery(CompanyProfilesQuery, {
    variables: {
      limit: numCompanyProfiles,
      hideReviews,
      hideJobs
    }
  });
  if (error) {
    console.error(error == null ? void 0 : error.message);
    onError == null ? void 0 : onError(error == null ? void 0 : error.message);
    return null;
  }
  const companyProfiles = (data == null ? void 0 : data.marqueeCompanies) ?? [];
  if (loading) {
    return /* @__PURE__ */ jsx(
      Placeholder,
      {
        onReviewsLandingPage,
        hideReviews,
        hideJobs
      }
    );
  }
  if (!companyProfiles.length) {
    return null;
  }
  const discoverMoreUrlWithQueryParam = buildUrlWithQueryParams(
    discoverMoreUrl,
    analyticsQueryParams ?? ""
  );
  const writeAReviewUrlWithQueryParam = buildUrlWithQueryParams(
    writeAReviewUrl,
    analyticsQueryParams ?? ""
  );
  const sectionSpace = onReviewsLandingPage ? "medium" : "large";
  return /* @__PURE__ */ jsxs(Box, { component: "section", children: [
    /* @__PURE__ */ jsxs(Stack, { space: sectionSpace, children: [
      /* @__PURE__ */ jsx(
        Header,
        {
          onReviewsLandingPage,
          writeAReviewUrl: writeAReviewUrlWithQueryParam
        }
      ),
      /* @__PURE__ */ jsx(
        Carousel,
        {
          companyProfiles,
          onReviewsLandingPage,
          discoverMoreUrl: discoverMoreUrlWithQueryParam,
          showReviews: !hideReviews,
          showJobs: !hideJobs,
          analyticsQueryParams,
          payload
        }
      )
    ] }),
    !onReviewsLandingPage ? /* @__PURE__ */ jsx(Box, { paddingTop: { mobile: sectionSpace, desktop: "none" }, children: /* @__PURE__ */ jsx(Inline, { space: "none", children: /* @__PURE__ */ jsx(
      ButtonLink,
      {
        variant: "ghost",
        tone: "neutral",
        href: discoverMoreUrlWithQueryParam,
        icon: /* @__PURE__ */ jsx(IconArrow, { direction: "right", alignY: "lowercase" }),
        iconPosition: "trailing",
        onClick: () => {
          try {
            tealium.link("carousel_navigation_pressed", {
              brand: payload == null ? void 0 : payload.brand,
              currentPage: payload == null ? void 0 : payload.currentPage,
              isLoggedIn: payload == null ? void 0 : payload.isLoggedIn,
              siteCountry: payload == null ? void 0 : payload.siteCountry.toLowerCase(),
              siteLanguage: payload == null ? void 0 : payload.siteLanguage,
              loginId: payload == null ? void 0 : payload.loginId,
              zone: payload == null ? void 0 : payload.zone,
              experiments: payload == null ? void 0 : payload.experiments,
              siteSection: payload == null ? void 0 : payload.siteSection,
              carouselSection: payload == null ? void 0 : payload.carouselSection,
              linkText: "See more"
            });
          } catch (e) {
            console.error({
              message: "Company profile carousel tealium.link() error",
              error: e
            });
          }
        },
        children: t("See more")
      }
    ) }) }) : /* @__PURE__ */ jsx(Hidden, { above: "tablet", children: /* @__PURE__ */ jsx(Box, { paddingTop: sectionSpace, children: /* @__PURE__ */ jsx(Actions, { children: /* @__PURE__ */ jsx(Inline, { space: "none", children: /* @__PURE__ */ jsx(
      WriteAReviewButton,
      {
        writeAReviewUrl: writeAReviewUrlWithQueryParam,
        payload
      }
    ) }) }) }) })
  ] });
};
export {
  BrandCard,
  BrandLogo,
  SelectBrandPanel as BrandSelectPanel,
  BrandSelector,
  CompanyProfileCarousel,
  CoverImage,
  ListBrandsQuery,
  UploadAssetButton,
  UploadAssetButton2 as UploadButton,
  getBadges,
  uploadLogo
};
